import { markRaw } from 'vue';
import { CanvasTexture, LinearFilter, Material, Texture, Vector2 } from 'three';
import { getHipSpineLandmarks } from '@/api/hip-spine-study/hipSpineLandmark';
import type { HipSpineLandmarkApiObject } from '@/api/hip-spine-study/hipSpineLandmark';
import type { XRAY_HIP_SPINE_VIEW_IDS } from '@/api/xrays';
import { loadImageAsBlob } from '@/api/loadImage';
import { getMaxTextureSize } from '@/lib/threeScene/textures/safeTexture';
import { HIP_SPINE_POSTURE_LANDMARK_NAMES } from '@/views/3d-planner/hipSpine/hipSpineAnnotationDefs';
import { selectLumbarPair, type HighestLumbarSelection } from '@/domain/anatomy/hipSpineLandmarks';
import { computeAngleOverlays, type LandmarkMap } from '@/domain/anatomy/hipSpineAngleOverlays';
import type { HipSpinePostureViewState } from '@/views/3d-planner/hipSpine/types';
import { buildAnnotationGroups } from '@/views/3d-planner/hipSpine/buildAnnotationGroups';
import { buildAngleGroups } from '@/views/3d-planner/hipSpine/angles/buildAngleGroups';
import { logger } from '@/util/logger';
import type { StandardNode } from '@/lib/threeScene/updateNode';
import type { HipSpinePostureApiObject } from '@/api/hip-spine-study/hipSpinePosture';
import { makeHipSpineXRayMaterial } from '@/lib/threeScene/materials/hipSpineXRayMaterial';

const log = logger('hip-spine');

/**
 * Prune a posture's landmarks to the names that posture is allowed to render
 * (`HIP_SPINE_POSTURE_LANDMARK_NAMES`). Returns the input unchanged when the
 * posture has no allow-list entry. Pure — no mutation. Shared by the initial
 * load and the Reset-to-AI re-fetch so `viewState.landmarks` has one contract.
 */
export function filterPostureLandmarks(
    posture: (typeof XRAY_HIP_SPINE_VIEW_IDS)[number],
    landmarks: HipSpineLandmarkApiObject[],
): HipSpineLandmarkApiObject[] {
    const allowedNames = HIP_SPINE_POSTURE_LANDMARK_NAMES[posture];
    return allowedNames
        ? landmarks.filter((landmark) => allowedNames.has(landmark.name))
        : landmarks;
}

export function makePostureViewState(
    posture: (typeof XRAY_HIP_SPINE_VIEW_IDS)[number],
): HipSpinePostureViewState {
    return {
        posture,
        imageMaterial: markRaw(makeHipSpineXRayMaterial()),
        imageSize: null,
        landmarks: [],
        annotationGroups: [],
        angleGroups: [],
        angles: [],
    };
}

function disposeNode(node: StandardNode): void {
    if ('geometry' in node && node.geometry) {
        node.geometry.dispose();
    }
    if ('material' in node && node.material instanceof Material) {
        if ('map' in node.material && node.material.map) {
            node.material.map.dispose();
        }
        node.material.dispose();
    }
}

export function resetPostureViewState(state: HipSpinePostureViewState): void {
    for (const group of state.annotationGroups) {
        group.children.forEach(disposeNode);
    }
    state.annotationGroups = [];
    for (const group of state.angleGroups) {
        group.children.forEach(disposeNode);
    }
    state.angleGroups = [];
    state.angles = [];

    const texture = state.imageMaterial.uniforms.uTexture.value as Texture | null;
    if (texture) {
        texture.dispose();
        state.imageMaterial.uniforms.uTexture.value = null;
    }
    state.imageMaterial.needsUpdate = true;
    state.imageSize = null;
    state.landmarks = [];
}

type FemurRadii = { 1?: number; 2?: number };

/**
 * Resolve the femur head radii to render: the caller's override wins, otherwise
 * the posture API values, otherwise undefined (annotation code falls back to its
 * own default). Extracted so `rebuildPostureGeometry` stays within complexity caps.
 */
function resolveFemurRadii(
    posture: HipSpinePostureApiObject | undefined,
    femurRadiiOverride: FemurRadii | undefined,
): FemurRadii | undefined {
    if (femurRadiiOverride) return femurRadiiOverride;
    if (!posture) return undefined;
    return {
        1: posture.femur_1_head_radius ?? undefined,
        2: posture.femur_2_head_radius ?? undefined,
    };
}

/**
 * Rebuild annotation + angle group geometry from a display selection and the
 * posture API object. Disposes the previous groups' geometry before rebuilding.
 *
 * The lumbar pruning/selection decision lives with the caller: `displaySelection`
 * is an already-prepared `{ pair, landmarks }` describing exactly which lumbar
 * pair to render. When omitted, the rebuild defaults to the posture's AI-detected
 * `lumbar_vertebra_end_plate` (falling back to L1 only when no posture is supplied)
 * so annotation, angle, and overlay code all see the same display set — and the
 * initial geometry matches the edit ViewModel's `selectedLumbarLevel` default.
 *
 * Used by `loadPostureViewState` at initial load (posture-endplate default), and by
 * the edit ViewModel's `endDrag` to refresh angles after a landmark change. The optional
 * `femurRadiiOverride` lets the ViewModel preserve any user-edited radii across the
 * rebuild — without it, the rebuild would silently revert the circles to the API values.
 */
export function rebuildPostureGeometry(
    viewState: HipSpinePostureViewState,
    posture: HipSpinePostureApiObject | undefined,
    displaySelection?: HighestLumbarSelection,
    femurRadiiOverride?: FemurRadii,
): void {
    if (!viewState.imageSize) return;

    const selection =
        displaySelection ??
        selectLumbarPair(viewState.landmarks, posture?.lumbar_vertebra_end_plate ?? 'L1');
    const { pair, landmarks } = selection;
    if (landmarks.length === 0) return;

    const femurRadii = resolveFemurRadii(posture, femurRadiiOverride);

    for (const group of viewState.annotationGroups) group.children.forEach(disposeNode);
    for (const group of viewState.angleGroups) group.children.forEach(disposeNode);

    viewState.annotationGroups = buildAnnotationGroups(
        viewState.posture,
        landmarks,
        viewState.imageSize,
        femurRadii,
    );

    const { width, height } = viewState.imageSize;
    // Scene/draw map: y-up (image flipped) so the overlay geometry sits the
    // right way round in the Three.js scene.
    const drawMap: LandmarkMap = new Map(
        landmarks.map((landmark) => [
            landmark.name,
            new Vector2(landmark.current_x * width, (1 - landmark.current_y) * height),
        ]),
    );
    // Value map: y-down image frame, matching the angle formulas' coordinate
    // contract so signed angles (LL, PI, SS, APPT) come out with the correct
    // sign. Magnitudes are identical in either frame; only the sign depends on
    // it. Drawing still uses `drawMap`.
    const valueMap: LandmarkMap = new Map(
        landmarks.map((landmark) => [
            landmark.name,
            new Vector2(landmark.current_x * width, landmark.current_y * height),
        ]),
    );
    const rawAngles = computeAngleOverlays(
        valueMap,
        viewState.posture,
        pair ? [pair.anterior, pair.posterior] : [],
    );
    viewState.angles = rawAngles.map((angle) => ({ ...angle, visible: true }));
    viewState.angleGroups = buildAngleGroups(viewState.angles, drawMap, viewState.imageSize);
}

/**
 * Replace a posture's landmarks with a server-authoritative set (filtered to the
 * posture's allowed landmark names) and rebuild its geometry from server truth.
 * Shared by the save-apply and idle-refresh store actions. Does NOT touch the image.
 */
export function applyPostureData(
    viewState: HipSpinePostureViewState,
    postureObj: HipSpinePostureApiObject,
    landmarks: HipSpineLandmarkApiObject[],
): void {
    viewState.landmarks = filterPostureLandmarks(postureObj.posture, landmarks);
    rebuildPostureGeometry(viewState, postureObj);
}

const IMAGE_LOAD_MAX_RETRIES = 3;
const IMAGE_LOAD_RETRY_DELAY_MS = 1000;

/**
 * Fetch and decode an image blob, retrying on transient decode failures.
 * The server intermittently returns responses that cannot be decoded as images
 * (e.g. error pages, truncated data). A retry with re-fetch resolves this.
 */
async function fetchAndDecodeImage(fileUrl: string, signal: AbortSignal): Promise<ImageBitmap> {
    let lastError: unknown;
    for (let attempt = 1; attempt <= IMAGE_LOAD_MAX_RETRIES; attempt++) {
        const blob = await loadImageAsBlob(fileUrl, { signal });
        try {
            return await createImageBitmap(blob);
        } catch (err) {
            lastError = err;
            log.warning(
                'Image decode failed for %s (attempt %d/%d, blob %d bytes %s): %s',
                fileUrl,
                attempt,
                IMAGE_LOAD_MAX_RETRIES,
                blob.size,
                blob.type,
                err,
            );
            if (attempt < IMAGE_LOAD_MAX_RETRIES) {
                await new Promise((resolve) => setTimeout(resolve, IMAGE_LOAD_RETRY_DELAY_MS));
            }
        }
    }
    throw lastError;
}

async function loadImage(
    state: HipSpinePostureViewState,
    fileUrl: string,
    signal: AbortSignal,
): Promise<void> {
    // createImageBitmap fully decodes the image off-thread, guaranteeing the pixel
    // data is ready for GPU upload. This avoids the texSubImage2D "bad image data"
    // error that occurs when using data URLs + TextureLoader (which resolves on the
    // HTMLImageElement.onload event before the browser has finished decoding).
    const bitmap = await fetchAndDecodeImage(fileUrl, signal);
    const { width, height } = bitmap;

    if (width > 0 && height > 0) {
        state.imageSize = { width, height };
    }

    // Downscale to a canvas if the image exceeds the GPU's MAX_TEXTURE_SIZE,
    // otherwise draw at original size. Using a canvas as the texture source
    // ensures consistent behaviour across browsers.
    const maxSize = getMaxTextureSize();
    const needsDownscale = width > maxSize || height > maxSize;
    const scale = needsDownscale ? Math.min(maxSize / width, maxSize / height) : 1;
    const targetW = Math.floor(width * scale);
    const targetH = Math.floor(height * scale);

    const canvas = document.createElement('canvas');
    canvas.width = targetW;
    canvas.height = targetH;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
        bitmap.close();
        throw new Error('Could not create 2D canvas context for image rendering');
    }
    ctx.drawImage(bitmap, 0, 0, targetW, targetH);
    bitmap.close();

    const texture = new CanvasTexture(canvas);
    texture.generateMipmaps = false;
    texture.magFilter = LinearFilter;
    texture.minFilter = LinearFilter;

    state.imageMaterial.uniforms.uTexture.value = markRaw(texture);
    state.imageMaterial.needsUpdate = true;
}

/**
 * Load landmarks and X-ray image for a single posture, returning the populated view state.
 * Landmark loading is non-critical — a failure leaves landmarks empty but the image still loads.
 */
export async function loadPostureViewState(
    caseId: number,
    hipSpineStudyId: number,
    signal: AbortSignal,
    postureObj: HipSpinePostureApiObject,
): Promise<HipSpinePostureViewState> {
    const posture = postureObj.posture;
    const viewState = makePostureViewState(posture);
    const fileUrl = `cases/${caseId}/hip-spine-studies/${hipSpineStudyId}/files/${posture}`;

    try {
        const response = await getHipSpineLandmarks(caseId, hipSpineStudyId, posture);
        viewState.landmarks = filterPostureLandmarks(posture, response.landmarks);
    } catch (err) {
        log.warning(`Failed to load landmarks for ${posture}: ${err}`);
    }

    try {
        await loadImage(viewState, fileUrl, signal);
    } catch (err) {
        log.warning(`Failed to load X-ray image for ${posture}: ${err}`);
    }

    rebuildPostureGeometry(viewState, postureObj);

    return viewState;
}
